// ENG-6600: pure, unit-testable computation of the per-channel config hash
// the manager uses to decide whether to re-render an agent's channel
// `.mcp.json` entry (slack / telegram / msteams / direct-chat) on each poll.
//
// The hash is intentionally an "inputs + version" fingerprint. The
// `knownChannelConfigHashes` cache skips the (expensive) credential rewrite
// whenever the hash is unchanged, so the steady state is zero churn. The hash
// MUST therefore change whenever the rendered entry would change, including
// when the change is code-only (a new env var the adapter emits), not a
// config change. That code-only class is what the manual `writeVersion` knob
// historically tracked (v4–v9), and what folding the build-time agt CLI
// version (`cliVersion`) into the hash now covers structurally: every release
// that changes channel rendering auto-publishes a new CLI (ENG-5795), so the
// hash moves and the entry is re-rendered exactly once per self-update.
//
// See the call site in `manager-worker.ts` for why this does NOT cause restart
// storms (the `.mcp.json` drift watcher absorbs a value-only re-render without
// bouncing the session; an identical re-render is a content-hash no-op).

import { createHash } from 'node:crypto';

import { canonicalJson } from './canonical-json.js';
import { normalizeSenderPolicyForHash } from './sender-policy-normalize.js';

/**
 * ENG-8399: channel-config keys whose value is a SET of principal ids that
 * `/host/refresh` derives per-request and injects into the response config.
 * Their array order carries no meaning — the MCP children treat every one of
 * them as a membership test.
 *
 *   allowed_users        ENG-6035/6236 — slack diagnostic/restart allowlist
 *   diagnostic_chat_ids  ENG-6059      — telegram /investigate allowlist
 *   ping_allowed_users   ENG-6931      — slack /ping allowlist
 *   ping_allowed_chat_ids ENG-6931     — telegram /ping allowlist
 */
const DERIVED_PRINCIPAL_ID_LIST_KEYS = [
  'allowed_users',
  'diagnostic_chat_ids',
  'ping_allowed_users',
  'ping_allowed_chat_ids',
] as const;

/**
 * ENG-8399: value-blind canonicalisation of the derived principal-id sets in a
 * channel config before it enters the hash.
 *
 * The server resolves these lists from an unordered `organization_people` join,
 * so the same logical set could arrive permuted on each refresh. `canonicalJson`
 * sorts object keys but deliberately preserves array order (semantic arrays
 * depend on it), so that instability flowed straight into this hash: it
 * oscillated, every agent's credentials were rewritten as `content-changed`
 * every provisioning cycle, and agt-aws-1 respawned all 14 sessions every ~6
 * minutes.
 *
 * `principal-resolution.ts` removes the source of the disorder. This is the
 * belt-and-braces guard on the consumer side — the identical posture
 * `normalizeSenderPolicyForHash` took for ENG-7435 — so a reorder from ANY
 * resolver (including an API instance that predates the server fix) can never
 * again cause a false rewrite.
 *
 * Pure: never mutates the input. The manager still writes the raw, unsorted
 * arrays into the child's env; only the hash input is normalised.
 */
export function normalizeChannelConfigForHash(config: unknown): unknown {
  if (config === null || typeof config !== 'object' || Array.isArray(config)) return config;
  const src = config as Record<string, unknown>;

  let normalized: Record<string, unknown> | null = null;
  for (const key of DERIVED_PRINCIPAL_ID_LIST_KEYS) {
    const list = src[key];
    if (!Array.isArray(list)) continue;
    normalized ??= { ...src };
    // Copy before sort — Array.prototype.sort mutates in place, and the raw
    // array is still referenced by the credential-write path.
    normalized[key] = [...list].sort();
  }
  return normalized ?? config;
}

/**
 * The manual write-version knob. Bumping it forces a one-shot rewrite of every
 * agent's channel entries on the next poll. Used historically (v4-v9) when a
 * code-only rendering change had to reach already-deployed agents and there was
 * no release to ride. The `cliVersion` fold (ENG-6600) now subsumes this for
 * ordinary code-only changes; the constant remains as an explicit-intent
 * override (e.g. forcing a rewrite without cutting a release).
 */
export const CHANNEL_WRITE_VERSION = 9;

export interface ChannelConfigHashInput {
  /** The build-time agt CLI version (`agtCliVersion`); 'dev' under tsx. */
  cliVersion: string;
  /** The raw channel config object from /host/refresh. */
  config: unknown;
  /** Team settings folded into the hash (timezone, etc.). */
  team: unknown;
  /** Peer roster folded into the hash. */
  peers: unknown;
  /** Effective session mode ('persistent' | 'oneshot' | null). */
  sessionMode: unknown;
  /** Effective sender policy (or null/undefined). */
  senderPolicy: unknown;
  /**
   * ENG-9350: how the channel's bot tokens are delivered — 'file' (written to
   * `.env.integrations`) or 'spawn-env' (injected via `tmux -e`, evicted from
   * disk). Folded into the hash so flipping `spawn-inject-channel-secrets`
   * invalidates the cached config and RE-RUNS writeChannelCredentials: enabling
   * spawn-env must re-run to evict the on-disk token, and disabling it must
   * re-run to restore the token before the next spawn omits channelSpawnSecrets.
   * Without this a mode flip leaves the cached hash unchanged and the write is
   * skipped, so the token lingers on disk (enable) or the channel goes tokenless
   * (disable). Optional; absent folds to 'file' (today's on-disk delivery).
   */
  channelSecretDelivery?: 'file' | 'spawn-env';
}

/**
 * Compute the sha256 fingerprint that gates channel-credential rewrites.
 *
 * Deterministic: identical input (same `cliVersion` included) yields the same
 * digest, so an unchanged channel is never rewritten poll-to-poll. A different
 * `cliVersion` yields a different digest, so a CLI self-update re-renders the
 * entry once: that is the propagation guarantee for code-only env/args
 * additions (ENG-6600).
 */
export function computeChannelConfigHash(input: ChannelConfigHashInput): string {
  return createHash('sha256')
    .update(
      canonicalJson({
        writeVersion: CHANNEL_WRITE_VERSION,
        cliVersion: input.cliVersion,
        // ENG-8399: value-blind on the server-derived principal id SETS so a
        // reorder of the same principals can't oscillate this hash into a
        // false rewrite (and, downstream, a session respawn).
        config: normalizeChannelConfigForHash(input.config),
        team: input.team,
        peers: input.peers,
        sessionMode: input.sessionMode ?? null,
        // ENG-7435: value-blind on the team_principals id SETS so a reorder of
        // the same principals can't oscillate this hash into a false rewrite.
        senderPolicy: normalizeSenderPolicyForHash(input.senderPolicy),
        // ENG-9350: a `spawn-inject-channel-secrets` flip must invalidate the
        // cache so writeChannelCredentials re-runs (evict on enable / restore on
        // disable). Absent folds to 'file', so pre-ENG-9350 callers and the
        // file-delivery default keep a stable digest relative to each other.
        channelSecretDelivery: input.channelSecretDelivery ?? 'file',
      }),
    )
    .digest('hex');
}
